import React, { useEffect, useState, useMemo } from 'react'
import { Box, IconButton, TextField, Tooltip, Menu, MenuItem } from '@mui/material';
import LoadingUserPage from '../components/LoadingUserPage';
import UserCard from '../components/UserCard';
import GridOnIcon from '@mui/icons-material/GridOn';
import ListIcon from '@mui/icons-material/List';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import Table from '../components/Table'
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getUserList } from '../utils/api';
import Avatar from '@mui/material/Avatar';

const AVATAR_PALETTE = [
  { bg: 'rgba(34, 93, 153, 0.12)', color: 'rgb(34, 93, 153)' },
  { bg: 'rgba(217, 108, 60, 0.14)', color: 'rgb(217, 108, 60)' },
  { bg: 'rgba(99, 179, 100, 0.14)', color: 'rgb(70, 140, 72)' },
  { bg: 'rgba(147, 51, 234, 0.12)', color: 'rgb(126, 34, 206)' },
  { bg: 'rgba(216, 90, 48, 0.12)', color: 'rgb(153, 60, 29)' },
];

const getAvatarColors = (name) => {
  const index = (name?.charCodeAt(0) || 0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index];
};

function User() {
  const user_cache = useSelector(state => state.user)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const company_id = localStorage.getItem("company_id");
  const [userList, setUserList] = useState([])
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTermData] = useState('')
  const [filteredData, setFilteredData] = useState([]);
  const [isListViewTrueOrGridViewFalse, setIsListViewTrueOrGridViewFalse] = useState(false)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [sortAndFilter, setSortAndFilter] = useState({
    filter_type: '',
    name_sort_order: '',
    email_sort_order: ''
  })

  useEffect(() => {
    (async () => {
      try {
        setLoading(true)
        if (user_cache?.user_list.length) {
          setUserList(user_cache?.user_list)
          setFilteredData(user_cache?.user_list)
        } else {
          const response = await getUserList(company_id)
          if (response?.data) {
            const dataList = response?.data?.map((item, index) => (
              { ...item, id: index }
            ))
            setUserList(dataList)
            setFilteredData(dataList)
            dispatch({
              type: 'USER_DATA_LIST',
              payload: dataList
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  useMemo(() => {
    if (user_cache?.user_list.length > 0) {
      setUserList(user_cache?.user_list)
      setFilteredData(user_cache?.user_list)
    }
  }, [user_cache?.user_list?.length])

  useEffect(() => {
    let filteredRows = userList
    if (filteredRows.length > 0) {
      filteredRows = filteredRows.filter((row) => {
        const fullName = row.user_name;
        const email = row.email;
        const phoneNumber = row.phone_number;
        const searchTermLower = searchTerm.toLowerCase();
        return (
          (email && email.toLowerCase().includes(searchTermLower)) ||
          (phoneNumber && phoneNumber.toLowerCase().includes(searchTermLower)) ||
          fullName.toLowerCase().includes(searchTermLower)
        );
      });
    }
    setFilteredData(filteredRows)
  }, [searchTerm, userList])

  useEffect(() => {
    if (sortAndFilter.filter_type === 'user_name') {
      const sortedData = [...userList].sort((a, b) => {
        const propertyA = a[sortAndFilter.filter_type];
        const propertyB = b[sortAndFilter.filter_type];

        if (propertyA === undefined || propertyB === undefined) return 0;
        if (propertyA === null) return -1;
        if (propertyB === null) return 1;

        const valueA = propertyA.toLowerCase();
        const valueB = propertyB.toLowerCase();

        if (valueA < valueB) return sortAndFilter.name_sort_order === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortAndFilter.name_sort_order === 'asc' ? 1 : -1;
        return 0;
      });
      setFilteredData(sortAndFilter.name_sort_order === '' ? userList : sortedData)
    } else if (sortAndFilter.filter_type === 'phone_number') {
      const user_list = [...userList]
      user_list?.sort((a, b) => {
        if (a.phone_number && !b.phone_number) return -1;
        if (!a.phone_number && b.phone_number) return 1;
        if (a.phone_number && b.phone_number) return a.phone_number.localeCompare(b.phone_number);
        return 0;
      })
      user_list.sort((a, b) => {
        const aHasContactInfo = a.email && a.phone_number;
        const bHasContactInfo = b.email && b.phone_number;
        if (aHasContactInfo && !bHasContactInfo) return 1;
        else if (!aHasContactInfo && bHasContactInfo) return -1;
        else return 0;
      })
      setFilteredData(user_list)
    } else if (sortAndFilter.filter_type === 'email') {
      const result = userList.slice()
      const user_list = result.sort((a, b) => {
        if (!a.email && !b.email) return 0;
        else if (!a.email) return 1
        else if (!b.email) return -1;
        const emailComparison = a.email.localeCompare(b.email);
        return sortAndFilter.email_sort_order === 'asc' ? emailComparison : -emailComparison;
      })
      setFilteredData(sortAndFilter.email_sort_order === '' ? userList : user_list)
    } else if (sortAndFilter.filter_type === '') {
      setFilteredData(userList)
    }
  }, [sortAndFilter, userList])

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget)
  const handleFilterClose = () => setFilterAnchorEl(null)

  const toggleNameSort = () => {
    const next = sortAndFilter.filter_type === 'user_name'
      ? (sortAndFilter.name_sort_order === 'asc' ? 'desc' : sortAndFilter.name_sort_order === 'desc' ? '' : 'asc')
      : 'asc'
    setSortAndFilter({ filter_type: next ? 'user_name' : '', name_sort_order: next, email_sort_order: '' })
    handleFilterClose()
  }

  const toggleEmailSort = () => {
    const next = sortAndFilter.filter_type === 'email'
      ? (sortAndFilter.email_sort_order === 'asc' ? 'desc' : sortAndFilter.email_sort_order === 'desc' ? '' : 'asc')
      : 'asc'
    setSortAndFilter({ filter_type: next ? 'email' : '', name_sort_order: '', email_sort_order: next })
    handleFilterClose()
  }

  const togglePhoneSort = () => {
    const next = sortAndFilter.filter_type === 'phone_number' ? '' : 'phone_number'
    setSortAndFilter({ filter_type: next, name_sort_order: '', email_sort_order: '' })
    handleFilterClose()
  }

  const nameActive = sortAndFilter.filter_type === 'user_name'
  const emailActive = sortAndFilter.filter_type === 'email'
  const phoneActive = sortAndFilter.filter_type === 'phone_number'

  const nameDirIcon = nameActive && sortAndFilter.name_sort_order === 'desc'
    ? <ArrowDownwardIcon sx={{ fontSize: 15 }} /> : <ArrowUpwardIcon sx={{ fontSize: 15 }} />
  const emailDirIcon = emailActive && sortAndFilter.email_sort_order === 'desc'
    ? <ArrowDownwardIcon sx={{ fontSize: 15 }} /> : <ArrowUpwardIcon sx={{ fontSize: 15 }} />

  const menuItemSx = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    fontSize: '13.5px',
    fontWeight: active ? 600 : 500,
    color: active ? 'rgb(34, 93, 153)' : 'rgb(60, 60, 70)',
    backgroundColor: active ? 'rgba(34, 93, 153, 0.06)' : 'transparent',
  })

  const handleUserdetail = (item) => {
    localStorage.setItem('user_id', item.user_id)
    navigate(`/users/${item.user_id}`);
  }

  const renderLoading = () => {
    const elements = [];
    for (let row = 0; row < 3; row++) {
      const rowElements = [];
      for (let col = 0; col < 3; col++) {
        rowElements.push(
          <div key={`${row}-${col}`} style={{ margin: 10 }}>
            <LoadingUserPage />
          </div>
        );
      }
      elements.push(
        <div className='grid-template-columns' key={`row-${row}`} style={{ display: 'grid', gap: '5px', padding: '0px 0px' }}>
          {rowElements}
        </div>
      );
    }
    return elements;
  };

  const processedData = filteredData.map((user) => ({
    ...user,
    'Email/Phone': user.email || user.phone_number,
  }));

  const columns = [
    {
      field: 'user_name',
      headerName: 'Name',
      flex: 1.4,
      editable: false,
      renderCell: (rowData) => {
        const name = rowData?.row?.user_name || '';
        const { bg, color } = getAvatarColors(name);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '12px',
                fontWeight: 700,
                background: bg,
                color: color,
              }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>
            {name}
          </Box>
        );
      },
    },
    {
      field: 'Email/Phone',
      headerName: 'Email/Phone',
      flex: 1.2,
      editable: false,
    },
    {
      field: 'successful_scan_count',
      headerName: 'Scans',
      flex: 1.2,
      editable: false,
    },
    {
      field: 'View',
      headerName: 'Action',
      width: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (rowData) => (
        <button
          onClick={() => handleUserdetail(rowData?.row)}
          style={{
            fontWeight: 700,
            fontSize: '12.5px',
            textTransform: 'none',
            borderRadius: '20px',
            padding: '6px 16px',
            border: 'none',
            background: 'rgba(34, 93, 153, 0.1)',
            color: 'rgb(34, 93, 153)',
            cursor: 'pointer',
          }}
        >
          View
        </button>
      )
    }
  ];

  return (
    <div style={{ backgroundColor: '#f7f9fc', minHeight: '100vh', borderRadius: "12px" }}>
      <Box sx={{ padding: '25px 36px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          <TextField
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTermData(e.target.value)}
            sx={{
              width: 250,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(12, 61, 110, 0.05)',
              },
            }}
          />

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <Tooltip title="Filter">
              <IconButton
                onClick={handleFilterClick}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(12, 61, 110, 0.05)',
                  color: sortAndFilter.filter_type ? 'rgb(34, 93, 153)' : 'rgb(217, 108, 60)',
                }}
              >
                <FilterAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  minWidth: 190,
                  mt: 1,
                  boxShadow: '0 8px 24px rgba(12, 61, 110, 0.12)',
                  py: '6px',
                },
              }}
            >
              <MenuItem onClick={toggleNameSort} sx={menuItemSx(nameActive)}>
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
                <span style={{ flex: 1, marginLeft: 10 }}>Name</span>
                {nameDirIcon}
              </MenuItem>
              <MenuItem onClick={toggleEmailSort} sx={menuItemSx(emailActive)}>
                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                <span style={{ flex: 1, marginLeft: 10 }}>Email</span>
                {emailDirIcon}
              </MenuItem>
              <MenuItem onClick={togglePhoneSort} sx={menuItemSx(phoneActive)}>
                <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                <span style={{ flex: 1, marginLeft: 10 }}>Phone</span>
              </MenuItem>
            </Menu>

            <Tooltip title={isListViewTrueOrGridViewFalse ? 'Grid View' : 'List View'}>
              <IconButton
                onClick={() => setIsListViewTrueOrGridViewFalse((v) => !v)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(12, 61, 110, 0.05)',
                  color: 'rgb(34, 93, 153)',
                }}
              >
                {isListViewTrueOrGridViewFalse ? <GridOnIcon fontSize="small" /> : <ListIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </div>
        </Box>

        <Box>
          {!loading
            ? <div>
              {isListViewTrueOrGridViewFalse
                ? <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(12,61,110,0.07)',
                    overflow: 'hidden',
                  }}
                >
                  <Table rows={processedData} columns={columns} pageSize={10} />
                </div>
                : <div>
                  <div className='grid-template-columns' style={{ display: 'grid', gap: '16px' }}>
                    {filteredData && filteredData?.map((item, index) => (
                      <UserCard key={index} item={item} index={index} />
                    ))}
                  </div>
                  {filteredData && filteredData.length === 0 && <p className='align-center m-t-20'>There are no records to display</p>}
                </div>}
            </div>
            : renderLoading()}
        </Box>
      </Box>
    </div>
  )
}

export default User